import React from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { GanttTask } from '../../api/ganttTasks';
import styles from './PerformanceSummary.module.css';

/**
 * ScheduleStatusRow — two-column row for the Schedule tab.
 *
 * Left column:  Recently completed tasks (last N finished)
 * Right column: Upcoming tasks (next N incomplete, sorted by start date)
 *
 * Uses the same card / twoUp styles as the Summary tab for visual consistency.
 */
type Props = {
  tasks: GanttTask[];
  limit?: number;
};

const fmtDate = (iso: string | null): string => {
  if (!iso) return 'TBD';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString(undefined, {
      month: 'short', day: 'numeric',
    });
  } catch { return iso; }
};

const ScheduleStatusRow: React.FC<Props> = ({ tasks, limit = 5 }) => {
  const { t } = useAppTranslation('kpi');
  const today = new Date().toISOString().slice(0, 10);

  // Filter out milestones and summary rows for both lists
  const leafTasks = tasks.filter((tk) => !tk.isMilestone && !tk.isSummary);

  // Recently completed: 100% done, sorted by end date descending (most recent first)
  const completed = leafTasks
    .filter((tk) => tk.percentComplete >= 100)
    .sort((a, b) => {
      const aDate = a.endDate || a.startDate || '';
      const bDate = b.endDate || b.startDate || '';
      return bDate.localeCompare(aDate);
    })
    .slice(0, limit);

  // Upcoming: not yet done, sorted by start date ascending
  const upcoming = leafTasks
    .filter((tk) => tk.percentComplete < 100)
    .sort((a, b) => {
      const aDate = a.startDate || a.endDate || 'zzzz';
      const bDate = b.startDate || b.endDate || 'zzzz';
      return aDate.localeCompare(bDate);
    })
    .slice(0, limit);

  // Don't render if both lists are empty
  if (completed.length === 0 && upcoming.length === 0) return null;

  return (
    <div className={styles.twoUp}>
      {/* Recently Completed */}
      <section className={styles.card} aria-labelledby="sched-completed-title">
        <h3 id="sched-completed-title" className={styles.cardTitle}>
          {t('performanceSummary.recentlyCompleted.title') || 'Recently Completed'}
        </h3>
        {completed.length === 0 ? (
          <div className={styles.empty}>
            {t('performanceSummary.recentlyCompleted.empty') || 'No tasks completed yet.'}
          </div>
        ) : (
          <div className={styles.upcomingRows}>
            {completed.map((task) => (
              <div key={task.id} className={styles.upcomingRow}>
                <div className={styles.upcomingInfo}>
                  <span className={styles.upcomingName}>{task.taskName}</span>
                  <span className={styles.upcomingMeta}>
                    {task.endDate ? fmtDate(task.endDate) : fmtDate(task.startDate)}
                  </span>
                </div>
                <span className={styles.upcomingPct} style={{ color: 'var(--color-success)' }}>
                  ✓
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Upcoming Tasks */}
      <section className={styles.card} aria-labelledby="sched-upcoming-title">
        <h3 id="sched-upcoming-title" className={styles.cardTitle}>
          {t('performanceSummary.upcoming.title') || 'Upcoming Tasks'}
        </h3>
        {upcoming.length === 0 ? (
          <div className={styles.empty}>
            {t('performanceSummary.upcoming.empty') || 'No upcoming tasks.'}
          </div>
        ) : (
          <div className={styles.upcomingRows}>
            {upcoming.map((task) => {
              const overdue = task.endDate != null && task.endDate < today;
              const pctText = `${Math.round(task.percentComplete)}%`;

              return (
                <div key={task.id} className={styles.upcomingRow}>
                  <div className={styles.upcomingInfo}>
                    <span className={styles.upcomingName}>{task.taskName}</span>
                    <span className={styles.upcomingMeta}>
                      {fmtDate(task.startDate)}
                      {task.endDate ? ` – ${fmtDate(task.endDate)}` : ''}
                    </span>
                  </div>
                  <span className={`${styles.upcomingPct} ${overdue ? styles.milestoneOverdue : ''}`}>
                    {pctText}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default ScheduleStatusRow;
